import config from "@/lib/config";
import Cookies from "@/lib/cookies";
import debugLog from "@/lib/debugLog";
import getRemoteIpAddress from "@/lib/getRemoteIpAddress";
import { SOAPDataSource } from "apollo-datasource-soap";
import { LRUCache } from "next/dist/server/lib/lru-cache";
import { v4 as uuidv4 } from "uuid";

/**
 * @typedef {Object} RecommendResult
 * @property {string} elementId
 * @property {string} sessionId
 * @property {string} content
 */

/**
 * @type {Object} AbstractParamsAttributes
 * @property {string} accountId
 */

/**
 * @typedef {Object} AbstractParams
 * @property {AbstractParamsAttributes} attributes
 * @property {string} member - ユニークユーザーID
 * @property {string} ipAddress - IPアドレス
 * @property {string} location - アクセスしたページのURL
 * @property {string} userAgent - ユーザーエージェント
 */

/**
 * @type {string}
 */
const RTOASTER_API_URL = "https://rt.rtoaster.jp/api/engine?wsdl";

/**
 * Rtoasterとの通信クラス.
 *
 * @constructor
 */
class Rtoaster extends SOAPDataSource {
  constructor(options) {
    const cache = options.cache || new LRUCache({ max: 100 });
    super(RTOASTER_API_URL, { ...options, cache });
    this.context = options;
  }

  /**
   * track.
   *
   * @param {string} location
   * @param {string} referrer
   * @param {object} options
   * @returns {Promise<void>}
   */
  async track(location, referrer, options = null) {
    const params = (() => {
      const params = this.#abstractParams(location);
      if (referrer) {
        params.referrer = referrer;
      }
      if (options) {
        const list = Object.keys(options).map((key) => {
          return { attributes: { name: key }, $value: options[key] };
        });
        params.appKey = list;
      }
      return params;
    })();
    debugLog("rtoaster track", JSON.stringify(params, null, 2));

    return await this.invoke("track", {
      data: params,
    });
  }

  /**
   * recommend.
   *
   * @param {string} location
   * @param {string[]} elementId
   * @returns {Promise<RecommendResult[]>}
   */
  async recommend(location, elementId) {
    return await this.invoke("recommend", {
      query: { ...this.#abstractParams(location), ...{ elementId } },
    });
  }

  /**
   * get abstractParams.
   * @returns {AbstractParams}
   */
  #abstractParams(location) {
    const { req, res } = this.context;
    const member = (() => {
      if (req.userToken) {
        return req.userToken?.hashedUserId;
      } else {
        // uuidv4
        // cookieから読む、なければ生成して保存
        const cookies = new Cookies(req, res);
        const rtid = cookies.get(config.rtoaster.uuidCookieName);
        if (rtid) {
          return rtid;
        } else {
          const uuid = uuidv4();
          cookies.set({
            name: config.rtoaster.uuidCookieName,
            value: uuid,
          });
          return uuid;
        }
      }
    })();
    const ipAddress = getRemoteIpAddress(req);
    const userAgent = req?.deviceInfo?.ua;

    return {
      attributes: {
        accountId: config.rtoaster.accountId,
      },
      member,
      ipAddress,
      userAgent,
      location,
    };
  }
}
export default Rtoaster;
